import { useState } from "react";
import {
  Card,
  Table,
  Badge,
  Text,
  Group,
  ScrollArea,
  Button,
  Modal,
  Stack,
  TextInput,
  Alert,
  Code,
  CopyButton,
} from "@mantine/core";
import { IconPlus, IconAlertTriangle } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { apiKeysApi } from "../lib/api";
import { ServiceApiKey } from "../types";
import { EmptyState } from "./EmptyState";
import { LoadingOverlay } from "./LoadingOverlay";
import { ConfirmationModal } from "./ConfirmationModal";
import { showSuccess, showError } from "../lib/notifications";

const NAMESPACE_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}$/;

function CreateKeyModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [namespace, setNamespace] = useState("");
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  const close = () => {
    setName("");
    setNamespace("");
    setCreatedKey(null);
    onClose();
  };

  const createMutation = useMutation({
    mutationFn: () => apiKeysApi.create({ name, namespace }),
    onSuccess: (res) => {
      setCreatedKey(res.data.key);
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
    onError: (error) => showError(error),
  });

  const namespaceValid = NAMESPACE_PATTERN.test(namespace);

  return (
    <Modal opened={opened} onClose={close} title="New API key" size="lg">
      {createdKey ? (
        <Stack>
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
            Copy this key now. It won't be shown again.
          </Alert>
          <Code block style={{ wordBreak: "break-all", whiteSpace: "pre-wrap" }}>
            {createdKey}
          </Code>
          <Group position="right">
            <CopyButton value={createdKey}>
              {({ copied, copy }) => (
                <Button color={copied ? "teal" : undefined} onClick={copy}>
                  {copied ? "Copied" : "Copy key"}
                </Button>
              )}
            </CopyButton>
            <Button variant="default" onClick={close}>
              Done
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack>
          <TextInput
            label="Name"
            placeholder="recruitment-os backend"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <TextInput
            label="Namespace"
            description="The key can only read and write files in this namespace."
            placeholder="recruitment-os"
            value={namespace}
            onChange={(e) => setNamespace(e.currentTarget.value.toLowerCase())}
            error={namespace && !namespaceValid ? "Lowercase letters, digits and dashes" : undefined}
          />
          <Group position="right">
            <Button variant="default" onClick={close}>
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!name.trim() || !namespaceValid}
              loading={createMutation.isLoading}
            >
              Create key
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

// Service API keys for the file API; shown as a tab on the Files page (SUPERUSER only).
export function FileApiKeysPanel() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ServiceApiKey | null>(null);

  const { data: keys = [], isLoading } = useQuery({
    queryKey: ["api-keys"],
    queryFn: async () => (await apiKeysApi.list()).data,
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id),
    onSuccess: () => {
      showSuccess("API key revoked.");
      setRevokeTarget(null);
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
    onError: (error) => showError(error),
  });

  return (
    <>
      <Group position="apart" mt="md">
        <Text size="sm" color="dimmed">
          Keys that let other services use the file API at /api/v1/files. Each key only
          reaches files in its namespace.
        </Text>
        <Button leftIcon={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
          New key
        </Button>
      </Group>

        {isLoading ? (
          <LoadingOverlay message="Loading..." />
        ) : keys.length === 0 ? (
          <EmptyState
            title="No API keys yet"
            description="Create a key for each service that needs to upload or read files."
          />
        ) : (
          <Card mt="md" shadow="sm" radius="md" withBorder>
            <ScrollArea>
              <Table striped highlightOnHover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Namespace</th>
                    <th>Key</th>
                    <th>Status</th>
                    <th>Last used</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((key) => (
                    <tr key={key.id}>
                      <td>
                        <Text size="sm" weight={500}>
                          {key.name}
                        </Text>
                      </td>
                      <td>
                        <Text size="sm">{key.namespace}</Text>
                      </td>
                      <td>
                        <Code>{key.prefix}_…</Code>
                      </td>
                      <td>
                        {key.revokedAt ? (
                          <Badge color="red">revoked</Badge>
                        ) : (
                          <Badge color="teal">active</Badge>
                        )}
                      </td>
                      <td>
                        <Text size="sm" color="dimmed">
                          {key.lastUsedAt
                            ? formatDistanceToNow(new Date(key.lastUsedAt), { addSuffix: true })
                            : "never"}
                        </Text>
                      </td>
                      <td>
                        <Text size="sm" color="dimmed">
                          {formatDistanceToNow(new Date(key.createdAt), { addSuffix: true })}
                          {key.createdBy ? ` by ${key.createdBy.email}` : ""}
                        </Text>
                      </td>
                      <td>
                        {!key.revokedAt && (
                          <Button
                            size="xs"
                            variant="subtle"
                            color="red"
                            onClick={() => setRevokeTarget(key)}
                          >
                            Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ScrollArea>
          </Card>
        )}

      <CreateKeyModal opened={createOpen} onClose={() => setCreateOpen(false)} />

      <ConfirmationModal
        opened={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={() => revokeTarget && revokeMutation.mutate(revokeTarget.id)}
        title="Revoke API key"
        message={`Requests using "${revokeTarget?.name}" will be rejected immediately. This can't be undone.`}
        confirmLabel="Revoke"
        isLoading={revokeMutation.isLoading}
      />
    </>
  );
}
